import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  MainFormErrorMessage,
} from '@/components/ui/form'
import { FormInput } from '@/components/ui/form/FormInput'
import { SecretInput } from '@/components/ui/secret-input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { orpc } from '@/lib/orpc/client'
import { queryClient } from '@/lib/query'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  createIntegrationSchema,
  type CreateIntegrationSchema,
} from '@repo/shared/schemas/integrations'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, ExternalLink, Send } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { toast } from 'sonner'

export const Route = createFileRoute(
  '/_dashboard/settings/integrations/shopify',
)({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    await context.queryClient.ensureQueryData(
      orpc.integrations.getIntegrations.queryOptions(),
    )
  },
})

interface SetupStepProps {
  stepNumber: number
  title: string
  children: ReactNode
}

function SetupStep({ stepNumber, title, children }: SetupStepProps) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-semibold">
        {stepNumber}
      </div>
      <div className="space-y-2">
        <h4 className="font-medium text-gray-900">{title}</h4>
        <div className="text-sm text-muted-foreground space-y-2">
          {children}
        </div>
      </div>
    </div>
  )
}

function ShopifySetupGuide() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>How to generate a Shopify API key</CardTitle>
        <CardDescription>
          Follow these steps in your Shopify admin to create an Admin API access
          token for Rocket Rank.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        <SetupStep stepNumber={1} title="Enable custom app development">
          <p>
            In your Shopify admin, go to{' '}
            <strong>Settings → Apps and sales channels</strong> and click
            <strong> Develop apps</strong>. If prompted, allow custom app
            development for your store.
          </p>
        </SetupStep>
        <SetupStep stepNumber={2} title="Create or select a custom app">
          <p>
            Create a new custom app (for example, “Rocket Rank”) and open the{' '}
            <strong>Configuration</strong> tab. Under{' '}
            <strong>Admin API access scopes</strong>, grant read access to the
            resources Rocket Rank should manage.
          </p>
        </SetupStep>
        <SetupStep stepNumber={3} title="Reveal the Admin API access token">
          <p>
            In the app’s <strong>API credentials</strong> section, click{' '}
            <strong>Install app</strong> and then
            <strong> Reveal token once</strong>. Copy the token and paste it in
            the field above. Keep it confidential—Shopify will not show it
            again.
          </p>
        </SetupStep>

        <div className="flex flex-col gap-2 text-sm text-muted-foreground">
          <span className="font-medium text-gray-900">Helpful resources</span>
          <Button
            variant="link"
            asChild
            className="justify-start px-0 h-auto text-primary"
          >
            <a
              href="https://help.shopify.com/en/manual/apps/app-types/custom-apps"
              target="_blank"
              rel="noreferrer"
            >
              Shopify documentation on creating custom apps
              <ExternalLink className="ml-2 w-4 h-4" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function SelectBlog() {
  const form = useFormContext<CreateIntegrationSchema>()
  const apiKey = form.watch('config.apiKey')
  const storeUrl = form.watch('config.storeUrl')
  const blogId = form.watch('config.blogId')

  const [debouncedApiKey, setDebouncedApiKey] = useState(apiKey || '')
  const [debouncedStoreUrl, setDebouncedStoreUrl] = useState(storeUrl)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedApiKey(apiKey)
      setDebouncedStoreUrl(storeUrl)
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [apiKey, storeUrl])

  const trimmedDebouncedApiKey = debouncedApiKey.trim()
  const trimmedDebouncedStoreUrl = debouncedStoreUrl.trim()

  const getBlogs = useQuery(
    orpc.integrations.shopify.getBlogs.queryOptions({
      input: {
        shopUrl: trimmedDebouncedStoreUrl,
        accessToken: trimmedDebouncedApiKey,
      },
      enabled:
        trimmedDebouncedApiKey.length > 0 &&
        trimmedDebouncedStoreUrl.length > 0,
    }),
  )

  if (!getBlogs.data || getBlogs.isError) {
    const hasError = getBlogs.isError
    return (
      <FormItem>
        <FormLabel>Blog</FormLabel>
        <FormControl>
          <Select disabled>
            <SelectTrigger className="w-full">
              {hasError ? (
                <SelectValue placeholder="Failed to load. Check your API key and store URL." />
              ) : (
                <SelectValue placeholder="Loading..." />
              )}
            </SelectTrigger>
          </Select>
        </FormControl>
        <FormDescription>
          This is the blog where Rocket Rank will publish posts.
        </FormDescription>
        <FormMessage />
      </FormItem>
    )
  }

  return (
    <FormField
      control={form.control}
      name="config.blogId"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Blog</FormLabel>
          <FormControl>
            <Select
              {...field}
              onValueChange={(val) => field.onChange(val)}
              defaultValue={blogId || undefined}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a blog" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {getBlogs.data.blogs.map((blog) => (
                    <SelectItem key={blog.id} value={blog.id.toString()}>
                      {blog.title}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </FormControl>
          <FormDescription>
            This is the blog where Rocket Rank will publish posts.
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

function RouteComponent() {
  const integrationsQuery = useQuery(
    orpc.integrations.getIntegrations.queryOptions({
      select: (integrations) =>
        integrations.find(
          (integration) => integration.config.type === 'shopify',
        ) ?? null,
    }),
  )

  const form = useForm<CreateIntegrationSchema>({
    resolver: zodResolver(createIntegrationSchema),
    mode: 'onChange',
    defaultValues: {
      config: {
        type: 'shopify',
        apiKey: '',
        storeUrl: '',
        blogId: '',
      },
      enabled: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    },
  })

  useEffect(() => {
    const integration = integrationsQuery.data
    if (integration && integration.config.type === 'shopify') {
      form.reset({
        config: {
          type: 'shopify',
          apiKey: integration.config.apiKey || '',
          storeUrl: integration.config.storeUrl || '',
          blogId: integration.config.blogId || '',
        },
        enabled: integration.enabled,
        createdAt: new Date(integration.createdAt),
        updatedAt: new Date(integration.updatedAt),
      })
    }
  }, [form, integrationsQuery.data])

  const createIntegrationMutation = useMutation(
    orpc.integrations.createIntegration.mutationOptions({
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.integrations.getIntegrations.queryKey(),
        })
      },
    }),
  )

  const apiKeyValue = form.watch('config.apiKey')
  const isSaving = createIntegrationMutation.isPending

  const onSubmit = async (data: CreateIntegrationSchema) => {
    if (data.config.type !== 'shopify') {
      toast.error('Unsupported integration type', {
        description: 'Please refresh and try again.',
      })
      return
    }

    const trimmedApiKey = data.config.apiKey.trim()

    if (!trimmedApiKey) {
      form.setError('config.apiKey', {
        type: 'manual',
        message: 'API key is required',
      })
      return
    }

    try {
      await createIntegrationMutation.mutateAsync({
        ...data,
        config: {
          type: 'shopify',
          apiKey: trimmedApiKey,
          storeUrl: data.config.storeUrl,
          blogId: data.config.blogId,
          authorName: data.config.authorName,
        },
      })

      toast.success('Shopify integration saved', {
        description: 'Rocket Rank will use this API key for future syncs.',
      })
    } catch (error) {
      console.error('Failed to save Shopify integration', error)
      toast.error('Unable to save Shopify integration', {
        description:
          'Please try again or contact support if the issue persists.',
      })
    }
  }

  const canSubmit = !isSaving && apiKeyValue.trim().length > 0

  return (
    <div className="max-w-4xl space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" asChild>
            <Link to="/settings/integrations">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Shopify Integration
            </h1>
            <p className="text-sm text-muted-foreground">
              Connect your Shopify store to Rocket Rank with a private Admin API
              access token.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>API key</CardTitle>
          <CardDescription>
            Paste the Admin API access token generated in Shopify. You can
            disable the integration at any time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormInput
                control={form.control}
                name="config.storeUrl"
                label="Shopify Store URL"
                placeholder="https://your-store.myshopify.com"
                description="This is the URL of your Shopify store (without https://)."
                formInputProps={{
                  spellCheck: false,
                  autoComplete: 'off',
                  disabled: isSaving,
                }}
              />

              <FormField
                control={form.control}
                name="config.apiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Shopify API Key</FormLabel>
                    <FormControl>
                      <SecretInput {...field} id="apiKey" />
                    </FormControl>
                    <FormDescription>
                      Paste the Admin API access token from Shopify.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormInput
                control={form.control}
                name="config.authorName"
                label="Author Name"
                placeholder="Admin"
                description="The name that will appear as the author of published articles."
                formInputProps={{
                  spellCheck: false,
                  autoComplete: 'off',
                  disabled: isSaving,
                }}
              />

              <SelectBlog />

              <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3 rounded-lg border p-4">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isSaving}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Enabled</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        When enabled, Rocket Rank syncs content with your
                        Shopify storefront.
                      </p>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <MainFormErrorMessage />

              <div className="flex justify-end">
                <Button type="submit" disabled={!canSubmit}>
                  <Send className="w-4 h-4" />
                  {isSaving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <ShopifySetupGuide />
    </div>
  )
}
